import { getFaqCategoriesAPI, postFaqAPI } from '@/src/apis/rest/faq';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { PostFaqRequestBody } from '@/src/apis/rest/faq.dtos';
import { NAV_CONST } from '@/src/constants/nav';
import Select from '@/src/components/atom/Select';
import TextArea from '@/src/components/atom/TextArea';
import { usePrompt } from '@/src/hooks/usePrompt';
import { OPTIONS_CONST } from '@/src/constants/options';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { useRef } from 'react';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';

const BACK_PATH = PATH.FAQ_LIST;

const formErrorMessages = {
  title: '제목을 입력해주세요.',
  contents: '내용을 입력해주세요.',
};

const faqFormSchema = () => {
  return z.object({
    title: z.string().min(1, formErrorMessages.title),
    contents: z.string().min(1, formErrorMessages.contents),
    faqCategoryType: z.string(),
    showedYn: z.boolean(),
  });
};

const DEFAULT_VALUES: Partial<PostFaqRequestBody> = {
  title: '',
  contents: '',
  faqCategoryType: undefined,
  showedYn: true,
};

export default function FaqCreatePage() {
  // util
  const navigate = useNavigate();
  const { toast } = useToast();

  // form
  const form = useForm<PostFaqRequestBody>({
    resolver: zodResolver(faqFormSchema()),
    defaultValues: DEFAULT_VALUES,
    reValidateMode: 'onSubmit',
  });

  // state
  const {
    Dialog: SaveDialog,
    isOpen: isSaveOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // query
  const { data: faqCategories } = useQuery({
    queryKey: ['faq-category'],
    queryFn: getFaqCategoriesAPI,
  });

  // mutation
  const postFaqMutation = useMutation({ mutationFn: postFaqAPI });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };
  const handleSubmit = (values: PostFaqRequestBody) => {
    postFaqMutation.mutate(values, {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: TOAST_CONST.saveSuccess.title,
          description: TOAST_CONST.saveSuccess.description,
        });

        navigate(BACK_PATH);
      },
      onError: e => {
        toast({
          variant: 'error',
          title: TOAST_CONST.error.title,
          description: e.message,
        });
      },
    });
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            disabled={!form.formState.isValid}
            type="button"
            onClick={openSaveDialog}
            className="w-40"
            buttonSize="medium"
            isLoading={postFaqMutation.isPending}
          >
            <div className="flex items-center gap-x-2">{BUTTON_CONST.save}</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  // usePrompt
  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.faq, NAV_CONST.faqAdd]}
        pageTitle={NAV_CONST.faqAdd}
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px]"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="FAQ 정보">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-6">
              <FormField
                control={form.control}
                name="faqCategoryType"
                render={({ field, fieldState: { error } }) => {
                  const { ref, ...restField } = field;

                  return (
                    <FormContent isRequired label="카테고리" error={error}>
                      <Select
                        rootProps={{
                          ...restField,
                          onValueChange: e => {
                            field.onChange(e);
                          },
                        }}
                        valueProps={{
                          placeholder: '카테고리를 선택해주세요',
                        }}
                        option={[
                          {
                            optionList: (faqCategories || []).map(item => ({
                              label: item,
                              value: item,
                            })),
                          },
                        ]}
                        ref={ref}
                      />
                    </FormContent>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="showedYn"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent
                      label="노출 여부"
                      className="col-span-3"
                      error={error}
                    >
                      <RadioGroup
                        optionList={[
                          { label: OPTIONS_CONST.showed, value: 'true' },
                          { label: OPTIONS_CONST.notShowed, value: 'false' },
                        ]}
                        rootProps={{
                          value: field.value ? 'true' : 'false',
                          onValueChange: (e: string) =>
                            field.onChange(e === 'true'),
                        }}
                      />
                    </FormContent>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="title"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent
                      className="col-span-3"
                      label="제목"
                      isRequired
                      error={error}
                    >
                      <Input
                        containerClassName="flex-1"
                        placeholder="제목을 입력해주세요"
                        {...field}
                      />
                    </FormContent>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="contents"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent
                      className="col-span-3"
                      label="내용"
                      isRequired
                      error={error}
                    >
                      <TextArea
                        placeholder="FAQ 내용을 입력하세요"
                        className="h-[500px]"
                        {...field}
                      />
                    </FormContent>
                  );
                }}
              />
            </div>
          </SectionCard>
          {renderBottomFixedContent()}
        </form>
      </Form>

      {/* 모달 */}
      {isSaveOpen && (
        <SaveDialog
          open={isSaveOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openSaveDialog();
            } else {
              closeSaveDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.save}
          isLoading={postFaqMutation.isPending}
        />
      )}
    </>
  );
}
